import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/router';

import { css } from '@emotion/react';
import { mq } from '@hangar31/rc-layout';
import Icon from '@OnArk/SVGs/Icon';
import { capture } from '@OnArk/utils/posthog';
import { useIsDesktopViewport } from '@OnArk/utils/useIsDesktopViewport';
import { withClient } from '@OnArk/withGraph';

const STORAGE_KEY = 'onark-desktop-handoff-dismissed-v1';

type State = 'idle' | 'sending' | 'sent';

/**
 * Mobile-only "Continue on desktop" handoff. Visible inside the section
 * editor at viewports below md. Tapping fires the sendDesktopLink
 * mutation which emails the signed-in user a link back to the class
 * they're working on; they sign in normally on desktop.
 *
 * Dismissed dismissals are persisted to localStorage so we don't nag.
 *
 * The banner is hidden above md by BOTH a `display` media query and an early
 * return here. The early return is the load-bearing one: `display: none`
 * still mounts the component, so the old mount-effect fired
 * `desktopHandoffShown` for every desktop admin who opened the section
 * editor — 539 of 593 impressions over 60 days came from viewports that
 * could never render it, which made the shown → requested funnel read ~0%
 * when the real mobile tap-through was ~24%.
 */
function DesktopHandoffBannerInner({
  client,
}: {
  client: Sdk;
}): React.JSX.Element {
  const router = useRouter();
  const isDesktop = useIsDesktopViewport();
  const [dismissed, setDismissed] = useState(true);
  const [state, setState] = useState<State>('idle');
  const [error, setError] = useState<string | null>(null);
  const shownCapturedRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      setDismissed(window.localStorage.getItem(STORAGE_KEY) === '1');
    } catch {
      // localStorage can throw in private browsing — treat as not dismissed.
      setDismissed(false);
    }
  }, []);

  // Count an impression only once the banner is actually on screen: not
  // dismissed AND below the md breakpoint. Resizing a desktop window down
  // genuinely reveals it, so that counts too — but only once per mount.
  useEffect(() => {
    if (shownCapturedRef.current || dismissed || isDesktop) return;

    shownCapturedRef.current = true;
    capture('desktopHandoffShown');
  }, [dismissed, isDesktop]);

  if (dismissed || isDesktop) return <></>;

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
    capture('desktopHandoffDismissed');
  };

  const send = async () => {
    if (state !== 'idle') return;
    setState('sending');
    setError(null);
    capture('desktopHandoffRequested');
    try {
      const courseId = router.query.courseId as string | undefined;
      const sectionId = router.query.sectionId as string | undefined;

      await client.sendDesktopLink({ courseId, sectionId });
      setState('sent');
      capture('desktopHandoffEmailSent');
      // Auto-dismiss the banner once the email is sent so it doesn't
      // linger after the success state has been seen.
      window.setTimeout(() => {
        try {
          window.localStorage.setItem(STORAGE_KEY, '1');
        } catch {
          // ignore
        }
        setDismissed(true);
      }, 6000);
    } catch (e) {
      setState('idle');
      const message = e instanceof Error ? e.message : '';

      setError(message || 'Could not send link. Try again?');
    }
  };

  return (
    <div
      css={theme =>
        mq({
          alignItems: 'flex-start',
          background: 'rgba(123, 44, 255, 0.06)',
          border: `1px solid rgba(123, 44, 255, 0.2)`,
          borderRadius: theme.radius.md,
          color: theme.colors.clickable,
          columnGap: '1rem',
          display: ['grid', undefined, 'none'],
          fontSize: '1.4rem',
          gridTemplateColumns: 'auto 1fr auto',
          lineHeight: 1.45,
          margin: '0 0 1.2rem',
          padding: '1rem 1.2rem',
        })
      }
    >
      <Icon name="ACTIONS_OpenTab" size="20" />
      <div>
        {state === 'sent' ? (
          <strong>Check your email — we sent a link.</strong>
        ) : (
          <>
            <strong>Building on a phone?</strong> We&rsquo;ll email you a link
            to pick this up on your laptop.
            {error && (
              <div
                css={theme => ({
                  color: theme.colors.error,
                  fontSize: '1.2rem',
                  marginTop: '0.4rem',
                })}
              >
                {error}
              </div>
            )}
            <div
              css={css`
                margin-top: 0.6rem;
                display: flex;
                gap: 0.8rem;
                align-items: center;
              `}
            >
              <button
                css={theme => css`
                  padding: 0.6rem 1.2rem;
                  font-size: 1.3rem;
                  font-weight: 600;
                  color: ${theme.colors.white};
                  background: ${theme.colors.clickable};
                  border: 0;
                  border-radius: 0.6rem;
                  cursor: pointer;
                  &[disabled] {
                    cursor: wait;
                    opacity: 0.6;
                  }
                `}
                disabled={state === 'sending'}
                type="button"
                onClick={() => void send()}
              >
                {state === 'sending' ? 'Sending…' : 'Email me a link'}
              </button>
              <button
                css={theme => css`
                  padding: 0;
                  font-size: 1.3rem;
                  color: ${theme.colors.grayDark};
                  background: transparent;
                  border: 0;
                  cursor: pointer;
                `}
                type="button"
                onClick={dismiss}
              >
                Not now
              </button>
            </div>
          </>
        )}
      </div>
      <button
        aria-label="Dismiss"
        css={theme => css`
          padding: 0;
          color: ${theme.colors.grayMed};
          background: transparent;
          border: 0;
          cursor: pointer;
          line-height: 0;
        `}
        type="button"
        onClick={dismiss}
      >
        <Icon name="ACTIONS_Close" size="14" />
      </button>
    </div>
  );
}

export default withClient(DesktopHandoffBannerInner);
